import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { Notification, NotificationPreferences, NotificationType, NotificationPriority, NotificationEntityType } from '../types/notifications'

const NOTIFICATIONS_COLLECTION = 'notifications'
const NOTIFICATION_PREFERENCES_COLLECTION = 'notification_preferences'

export type NotificationPayload = {
  user_id: string // Required
  agency_id: string // Required for RLS
  type: NotificationType // Required
  title: string // Required
  message?: string | null
  priority?: NotificationPriority | null
  action_url?: string | null
  related_entity_type?: NotificationEntityType | null
  related_entity_id?: string | null
}

function mapNotification(record: Record<string, any>): Notification {
  return {
    id: record.id,
    user_id: record.user_id ?? '',
    agency_id: record.agency_id ?? '',
    type: (record.type ?? 'system') as NotificationType,
    title: record.title ?? '',
    message: record.message ?? null,
    priority: record.priority ?? null,
    read: record.read ?? false,
    read_at: record.read_at ?? null,
    action_url: record.action_url ?? null,
    related_entity_type: record.related_entity_type ?? null,
    related_entity_id: record.related_entity_id ?? null,
    snoozed_until: record.snoozed_until ?? null,
    dismissed: record.dismissed ?? false,
    created: record.created ?? '',
    updated: record.updated ?? '',
  }
}

function mapNotificationPreferences(record: Record<string, any>): NotificationPreferences {
  return {
    id: record.id,
    user_id: record.user_id ?? '',
    // Master channel toggles
    email_enabled: record.email_enabled ?? true,
    browser_enabled: record.browser_enabled ?? true,
    in_app_enabled: record.in_app_enabled ?? true,
    // Per-type enable/disable
    task_overdue: record.task_overdue ?? true,
    task_due_today: record.task_due_today ?? true,
    compliance_overdue: record.compliance_overdue ?? true,
    compliance_due_today: record.compliance_due_today ?? true,
    event_upcoming: record.event_upcoming ?? true,
    deal_stalled: record.deal_stalled ?? true,
    follow_up_due: record.follow_up_due ?? true,
    contract_expiring: record.contract_expiring ?? true,
    // Per-type channel control, read the way the jobs read it: in-app is on
    // unless switched off, email is off unless switched on. `?? true` used to
    // stand here, which meant a stored false showed as a switch the person
    // never touched reading on, while the job it gated stayed silent.
    task_email: record.task_email === true,
    task_in_app: record.task_in_app !== false,
    compliance_email: record.compliance_email === true,
    compliance_in_app: record.compliance_in_app !== false,
    deal_email: record.deal_email === true,
    deal_in_app: record.deal_in_app !== false,
    event_email: record.event_email === true,
    event_in_app: record.event_in_app !== false,
    follow_up_email: record.follow_up_email === true,
    follow_up_in_app: record.follow_up_in_app !== false,
    contract_email: record.contract_email === true,
    contract_in_app: record.contract_in_app !== false,
    injury_email: record.injury_email === true,
    injury_in_app: record.injury_in_app !== false,
    transaction_email: record.transaction_email === true,
    transaction_in_app: record.transaction_in_app !== false,
    capture_in_app: record.capture_in_app !== false,
    // Mail only to someone who asked for it, like every email switch here.
    digest_email: record.digest_email === true,
    digest_timezone: record.digest_timezone || null,
    // Configurable thresholds
    follow_up_days: record.follow_up_days ?? 21,
    stalled_deal_days: record.stalled_deal_days ?? 14,
    event_hours: record.event_hours ?? 72,
    // Timestamps
    created: record.created ?? record.created_at ?? '',
    updated: record.updated ?? record.updated_at ?? '',
  }
}

/**
 * PocketBase stores a date field as "YYYY-MM-DD HH:MM:SS.sssZ" — a space, not
 * the ISO "T" — and compares filter values as strings. ' ' (0x20) sorts below
 * 'T' (0x54), so a snooze ending later today reads as already past against an
 * ISO value and the notification resurfaces early.
 */
function pbNow(): string {
  return new Date().toISOString().replace('T', ' ')
}

export async function listNotifications(userId: string, options?: { read?: boolean; limit?: number; includeHidden?: boolean }) {
  
  const filter: string[] = [`user_id = "${escapeFilterValue(userId)}"`]
  if (options?.read !== undefined) {
    filter.push(`read = ${options.read}`)
  }

  // Exclude dismissed and currently-snoozed notifications by default
  if (!options?.includeHidden) {
    filter.push(`dismissed = false`)
    filter.push(`(snoozed_until = "" || snoozed_until <= "${escapeFilterValue(pbNow())}")`)
  }

  const params: any = {
    filter: filter.join(' && '),
    sort: '-created',
  }

  if (options?.limit) {
    params.limit = options.limit
  }

  const data = await pb.collection(NOTIFICATIONS_COLLECTION).getList<Record<string, any>>(1, options?.limit || 50, params)
  return data.items.map(mapNotification)
}

export async function getUnreadCount(userId: string): Promise<number> {
  
  const now = escapeFilterValue(pbNow())
  const data = await pb.collection(NOTIFICATIONS_COLLECTION).getList<Record<string, any>>(1, 1, {
    filter: `user_id = "${escapeFilterValue(userId)}" && read = false && dismissed = false && (snoozed_until = "" || snoozed_until <= "${now}")`,
  })
  return data.totalItems
}

export async function markAsRead(notificationId: string) {
  await pb.collection(NOTIFICATIONS_COLLECTION).update(notificationId, {
    read: true,
    read_at: new Date().toISOString(),
  })
}

export async function markAllAsRead(userId: string) {
  const notifications = await listNotifications(userId, { read: false })
  const updates = notifications.map(n =>
    pb.collection(NOTIFICATIONS_COLLECTION).update(n.id, {
      read: true,
      read_at: new Date().toISOString(),
    })
  )
  await Promise.all(updates)
}

export async function deleteNotification(notificationId: string) {
  await pb.collection(NOTIFICATIONS_COLLECTION).delete(notificationId)
}

/** Snoozing hides a notification until `untilDate`; it does not read it.
 *  The same row resurfaces, still unread, and still counts in the badge. */
export async function snoozeNotification(notificationId: string, untilDate: string) {
  await pb.collection(NOTIFICATIONS_COLLECTION).update(notificationId, {
    snoozed_until: untilDate,
  })
}

export async function dismissNotification(notificationId: string) {
  await pb.collection(NOTIFICATIONS_COLLECTION).update(notificationId, {
    dismissed: true,
    read: true,
    read_at: new Date().toISOString(),
  })
}

/**
 * Batch-fetch all dismissed entity IDs for a user.
 * Used to skip notification creation for permanently dismissed items.
 */
export async function getDismissedEntityIds(userId: string): Promise<Set<string>> {
  
  const data = await pb.collection(NOTIFICATIONS_COLLECTION).getFullList<Record<string, any>>({
    filter: `user_id = "${escapeFilterValue(userId)}" && dismissed = true && related_entity_id != ""`,
    fields: 'related_entity_id,type',
  })
  return new Set(data.map(r => `${r.type}:${r.related_entity_id}`))
}

/**
 * Batch-fetch all currently-snoozed entity IDs for a user.
 */
export async function getSnoozedEntityIds(userId: string): Promise<Set<string>> {
  
  const now = escapeFilterValue(new Date().toISOString())
  const data = await pb.collection(NOTIFICATIONS_COLLECTION).getFullList<Record<string, any>>({
    filter: `user_id = "${escapeFilterValue(userId)}" && snoozed_until != "" && snoozed_until > "${now}" && related_entity_id != ""`,
    fields: 'related_entity_id,type',
  })
  return new Set(data.map(r => `${r.type}:${r.related_entity_id}`))
}

export async function createNotification(payload: NotificationPayload) {
  const createPayload = {
    ...payload,
    read: false,
    read_at: null,
  }
  const record = await pb.collection(NOTIFICATIONS_COLLECTION).create(createPayload)
  return mapNotification(record)
}

/**
 * Check if a similar notification already exists for the same entity within the specified time window.
 */
export async function hasRecentNotification(
  userId: string,
  type: NotificationType,
  relatedEntityId: string,
  hoursWindow: number = 24
): Promise<boolean> {
  
  const windowStart = new Date(Date.now() - hoursWindow * 60 * 60 * 1000).toISOString()

  const data = await pb.collection(NOTIFICATIONS_COLLECTION).getList<Record<string, any>>(1, 1, {
    filter: `user_id = "${escapeFilterValue(userId)}" && type = "${escapeFilterValue(type)}" && related_entity_id = "${escapeFilterValue(relatedEntityId)}" && created >= "${escapeFilterValue(windowStart)}"`,
  })

  return data.totalItems > 0
}

/**
 * Create a notification only if a similar one doesn't already exist recently,
 * and the entity hasn't been dismissed or snoozed.
 * Accepts pre-fetched dismissed/snoozed sets for batch performance.
 */
export async function createNotificationIfNotExists(
  payload: NotificationPayload,
  hoursWindow: number = 24,
  dismissedEntities?: Set<string>,
  snoozedEntities?: Set<string>
): Promise<Notification | null> {
  if (payload.related_entity_id) {
    const entityKey = `${payload.type}:${payload.related_entity_id}`

    // Check dismissed (use pre-fetched set if available)
    if (dismissedEntities?.has(entityKey)) return null

    // Check snoozed (use pre-fetched set if available)
    if (snoozedEntities?.has(entityKey)) return null

    // Check for existing recent notification (dedup)
    const exists = await hasRecentNotification(
      payload.user_id,
      payload.type,
      payload.related_entity_id,
      hoursWindow
    )
    if (exists) return null
  }

  return await createNotification(payload)
}

/**
 * The row rule on notification_preferences is agency-scoped, so a row created
 * without agency_id is refused, and one that somehow exists is invisible to
 * its own user. Every create sends the caller's agency.
 */
function callerAgencyId(): string {
  const agencyId = (pb.authStore.model as { agency_id?: string } | null)?.agency_id
  if (!agencyId) throw new Error('Your account has no agency yet, so notification preferences cannot be saved.')
  return agencyId
}

export async function getNotificationPreferences(userId: string): Promise<NotificationPreferences | null> {
  
  const data = await pb.collection(NOTIFICATION_PREFERENCES_COLLECTION).getList<Record<string, any>>(1, 1, {
    filter: `user_id = "${escapeFilterValue(userId)}"`,
  })
  if (data.items.length === 0) {
    const defaultPrefs = await pb.collection(NOTIFICATION_PREFERENCES_COLLECTION).create({
      user_id: userId,
      agency_id: callerAgencyId(),
      email_enabled: true,
      browser_enabled: true,
      in_app_enabled: true,
      task_overdue: true,
      task_due_today: true,
      compliance_overdue: true,
      compliance_due_today: true,
      event_upcoming: true,
      deal_stalled: true,
      follow_up_due: true,
      contract_expiring: true,
      // Every channel column, written explicitly. PocketBase stores an omitted
      // boolean as false, and the jobs read an explicit false as "off", so a
      // row created without these silences its own user on their first app load.
      task_in_app: true,
      compliance_in_app: true,
      deal_in_app: true,
      event_in_app: true,
      follow_up_in_app: true,
      contract_in_app: true,
      injury_in_app: true,
      transaction_in_app: true,
      capture_in_app: true,
      // Email stays off until a person asks for it.
      task_email: false,
      compliance_email: false,
      deal_email: false,
      event_email: false,
      follow_up_email: false,
      contract_email: false,
      injury_email: false,
      transaction_email: false,
    })
    return mapNotificationPreferences(defaultPrefs)
  }
  return mapNotificationPreferences(data.items[0])
}

export async function updateNotificationPreferences(userId: string, preferences: Partial<NotificationPreferences>) {
  const existing = await getNotificationPreferences(userId)
  if (!existing) {
    const created = await pb.collection(NOTIFICATION_PREFERENCES_COLLECTION).create({
      user_id: userId,
      agency_id: callerAgencyId(),
      ...preferences,
    })
    return mapNotificationPreferences(created)
  }
  const updated = await pb.collection(NOTIFICATION_PREFERENCES_COLLECTION).update(existing.id, preferences)
  return mapNotificationPreferences(updated)
}
